import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  Menu,
  MenuItem,
  ListItemIcon as MuiListItemIcon,
  TextField,
  InputAdornment,
  FormControl,
  Select,
} from '@mui/material';
import { Refresh, Visibility, MoreVert, Search } from '@mui/icons-material';
import { getAllPaymentTransactions } from '../../services/Api';
import { useNavigate } from 'react-router-dom';

const GetAllPaymentTransactions = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);

    // Filters
    const [bookingId, setBookingId] = useState('');
    const [debouncedBookingId, setDebouncedBookingId] = useState('');
    const [filterStatus, setFilterStatus] = useState('--');
    const [filterGateway] = useState('');

    // Menu state
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuSelectedTx, setMenuSelectedTx] = useState(null);

    const navigate = useNavigate();

    // Debounce effect
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedBookingId(bookingId);
            setPage(0);
        }, 1000);
        return () => clearTimeout(handler);
    }, [bookingId]);

    const fetchTransactions = React.useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getAllPaymentTransactions(
                page + 1, 
                rowsPerPage, 
                debouncedBookingId, 
                filterStatus, 
                filterGateway
            );
            if (response.success || response.data) {
                const dataArr = response.data?.transactions || [];
                setTransactions(dataArr);
                setTotalRows(response?.totalRecords || response?.totalItems || response?.totalCount || response?.totalDocs || response?.total || response?.data?.totalRecords || response?.data?.totalItems || response?.data?.totalCount || response?.data?.totalDocs || response?.data?.total || response?.data?.totalElements || response?.data?.length || 0);
            } else {
                setError(response?.message || 'Failed to fetch payment transactions');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'An error occurred while fetching payment transactions');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, debouncedBookingId, filterStatus, filterGateway]);

    useEffect(() => {
        fetchTransactions();
    }, [page, rowsPerPage, debouncedBookingId, filterStatus, filterGateway, fetchTransactions]);

    const handleMenuOpen = (event, transaction) => {
        setAnchorEl(event.currentTarget);
        setMenuSelectedTx(transaction);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuSelectedTx(null);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 300, color: 'text.primary' }}>
                        Payment Transactions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        View and manage all payment transactions tracked across bookings
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <IconButton 
                        onClick={() => {
                            setTransactions([]);
                            fetchTransactions();
                        }} 
                        disabled={loading}
                        sx={{ color: 'primary.main', backgroundColor: 'rgba(120, 81, 169, 0.1)' }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : <Refresh />}
                    </IconButton>
                </Box>
            </Box>

            {/* Filters */}
            <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                        placeholder="Search Booking ID..."
                        size="small"
                        value={bookingId}
                        onChange={(e) => setBookingId(e.target.value)}
                        sx={{ width: 250 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select
                            value={filterStatus}
                            onChange={(e) => {
                                setFilterStatus(e.target.value);
                                setPage(0);
                            }}
                            displayEmpty
                        >
                            <MenuItem value="--"><em>All Statuses</em></MenuItem>
                            <MenuItem value="created">Created</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                            <MenuItem value="failed">Failed</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Paper>

            {error ? (
                <Alert severity="error" sx={{ mb: 4 }}>
                    {error}
                </Alert>
            ) : null}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <TableContainer>
                    <Table sx={{ minWidth: 650 }} aria-label="transactions table">
                        <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>S.No</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Booking ID</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && transactions.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : transactions.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                                        <Typography color="text.secondary">
                                            No payment transactions found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                transactions.map((tx, index) => (
                                    <TableRow key={tx.id || tx._id || index} hover>
                                        <TableCell sx={{ fontWeight: 500 }}>{page * rowsPerPage + index + 1}</TableCell>
                                        <TableCell>{tx.bookingId || '-'}</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>₹{tx.amount || 0}</TableCell>

                                        <TableCell>
                                            <Chip
                                                label={tx.status || 'unknown'}
                                                size="small"
                                                color={
                                                    (tx.status === 'completed' || tx.status === 'success') ? 'success' :
                                                    (tx.status === 'failed') ? 'error' :
                                                    'warning'
                                                }
                                                sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ textTransform: 'capitalize' }}>
                                            {tx.serviceType || '-'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={(e) => handleMenuOpen(e, tx)}>
                                                <MoreVert />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalRows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            {/* Actions Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                elevation={1}
                PaperProps={{
                    sx: {
                        minWidth: 150,
                        border: '1px solid',
                        borderColor: 'divider',
                        mt: 1,
                    }
                }}
            >
                <MenuItem onClick={() => {
                    navigate(`/payment-transactions/view/${menuSelectedTx?.id || menuSelectedTx?._id}`);
                    handleMenuClose();
                }}>
                    <MuiListItemIcon><Visibility fontSize="small" color="primary" /></MuiListItemIcon>
                    View Details
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default GetAllPaymentTransactions;
